import { useEffect } from "react";

/**
 * Global helper: for every `table.table-cards` in the page, copy the column
 * headers (<thead th>) onto each body cell as `data-label`, and tag the first
 * cell (`card-title`) and icon-only action cells (`card-actions`). This lets the
 * mobile card CSS render "label: value" rows without editing every <td> by hand.
 * Runs on mount and whenever the DOM changes (debounced via rAF).
 */
export function useCardTableLabels() {
  useEffect(() => {
    let raf = 0;
    let obs;

    const label = () => {
      document.querySelectorAll("table.table-cards").forEach((tbl) => {
        const ths = Array.from(tbl.querySelectorAll("thead th")).map((th) => th.textContent.trim());
        tbl.querySelectorAll("tbody tr, tfoot tr").forEach((tr) => {
          const tds = Array.from(tr.children).filter((c) => c.tagName === "TD");
          if (tds.length === 1 && tds[0].hasAttribute("colspan")) return; // empty-state row
          tds.forEach((td, i) => {
            if (td.getAttribute("colspan")) return;
            if (ths[i] !== undefined) td.setAttribute("data-label", ths[i]);
            if (i === 0) {
              td.classList.add("card-title");
            } else {
              const hasControls = td.querySelector("button, a");
              const txt = td.textContent.replace(/\s/g, "");
              if (hasControls && txt.length <= 2) td.classList.add("card-actions");
            }
          });
        });
      });
    };

    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (obs) obs.disconnect();
        label();
        if (obs) obs.observe(document.body, { childList: true, subtree: true });
      });
    };

    obs = new MutationObserver(schedule);
    schedule();
    obs.observe(document.body, { childList: true, subtree: true });

    return () => { if (obs) obs.disconnect(); cancelAnimationFrame(raf); };
  }, []);
}
